import { generateUUID } from "@/lib/utils";

const PENDING_CHAT_LAUNCH_KEY = "pending-chat-launch";
const PENDING_CHAT_LAUNCH_MAX_AGE_MS = 30 * 60 * 1000;

interface PendingChatLaunch {
  prompt: string;
  createdAt: number;
}

function readPendingChatLaunch(): PendingChatLaunch | null {
  if (typeof window === "undefined") return null;

  const raw = window.sessionStorage.getItem(PENDING_CHAT_LAUNCH_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingChatLaunch;
    if (
      typeof parsed?.prompt !== "string" ||
      !parsed.prompt.trim() ||
      typeof parsed?.createdAt !== "number"
    ) {
      window.sessionStorage.removeItem(PENDING_CHAT_LAUNCH_KEY);
      return null;
    }

    if (Date.now() - parsed.createdAt > PENDING_CHAT_LAUNCH_MAX_AGE_MS) {
      window.sessionStorage.removeItem(PENDING_CHAT_LAUNCH_KEY);
      return null;
    }

    return {
      prompt: parsed.prompt.trim(),
      createdAt: parsed.createdAt,
    };
  } catch {
    window.sessionStorage.removeItem(PENDING_CHAT_LAUNCH_KEY);
    return null;
  }
}

export function buildNewChatLaunchUrl(prompt: string): string {
  const params = new URLSearchParams({
    id: generateUUID(),
    q: prompt,
  });

  return `/?${params.toString()}`;
}

export function storePendingChatLaunch(prompt: string): void {
  if (typeof window === "undefined") return;

  const trimmedPrompt = prompt.trim();
  if (!trimmedPrompt) return;

  const payload: PendingChatLaunch = {
    prompt: trimmedPrompt,
    createdAt: Date.now(),
  };

  window.sessionStorage.setItem(
    PENDING_CHAT_LAUNCH_KEY,
    JSON.stringify(payload)
  );
}

export function consumePendingChatLaunch(): PendingChatLaunch | null {
  const pending = readPendingChatLaunch();

  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(PENDING_CHAT_LAUNCH_KEY);
  }

  return pending;
}

export function clearPendingChatLaunch(): void {
  if (typeof window === "undefined") return;

  window.sessionStorage.removeItem(PENDING_CHAT_LAUNCH_KEY);
}
